import { useMemo, useState } from 'react';

import {
  GridFooterContainer,
  gridPageCountSelector,
  gridPageSizeSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import {
  Box,
  MenuItem,
  Pagination,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { debounce } from 'src/utils/timers';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';

export type OnPageSizeChange = (newPageSize: number) => void;
export type RowsPerPageOptions = number[];
export type OnPageChange = (pageNumber: number) => void;
export type OnPageInputChange = (pageInputValue: string) => void;

interface CustomDataGridPaginationProps {
  page: number;
  onPageSizeChange: OnPageSizeChange;
  rowsPerPageOptions: RowsPerPageOptions;
  onPageChange: OnPageChange;
  pageInput: string;
  onPageInputChange: OnPageInputChange;
}

const CustomDataGridPagination = ({
  page,
  onPageSizeChange,
  rowsPerPageOptions,
  onPageChange,
  pageInput,
  onPageInputChange,
}: CustomDataGridPaginationProps): JSX.Element => {
  const apiRef = useGridApiContext();

  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <GridFooterContainer
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: 2,
        paddingLeft: 3,
        paddingRight: 3,
        paddingBottom: 2,
      }}
    >
      <Box>
        <RowsPerPage
          onPageInputChange={onPageInputChange}
          onPageSizeChange={onPageSizeChange}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageChange={onPageChange}
        />
      </Box>
      <Box>
        <Pagination
          shape="rounded"
          count={pageCount}
          page={page}
          onChange={(_, value) => onPageChange(value)}
          showFirstButton
          showLastButton
        />
      </Box>
      <Box>
        <GoToPage
          onPageChange={onPageChange}
          pageInput={pageInput}
          onPageInputChange={onPageInputChange}
        />
      </Box>
    </GridFooterContainer>
  );
};

interface RowsPerPageProps {
  rowsPerPageOptions: RowsPerPageOptions;
  onPageSizeChange: OnPageSizeChange;
  onPageChange: OnPageChange;
  onPageInputChange: OnPageInputChange;
}

const RowsPerPage = ({
  rowsPerPageOptions,
  onPageSizeChange,
  onPageChange,
  onPageInputChange,
}: RowsPerPageProps): JSX.Element => {
  const apiRef = useGridApiContext();
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);

  const handleChange = (event: SelectChangeEvent<number>) => {
    apiRef.current.setPageSize(Number(event.target.value));
    onPageSizeChange(Number(event.target.value));
    onPageChange(1);
    onPageInputChange('');
  };

  return (
    <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
      <Typography sx={{ typography: 'body2' }}>Show</Typography>

      <Select
        className="resultsPerPage"
        value={pageSize}
        onChange={handleChange}
        sx={{
          marginLeft: 1,
          width: 30,
          height: 30,
          typography: 'body2',
          fontWeight: 'bold',
        }}
      >
        {rowsPerPageOptions.map((value) => (
          <MenuItem key={String(value)} value={value}>
            {value}
          </MenuItem>
        ))}
      </Select>
      <Typography sx={{ marginLeft: 1, typography: 'body2' }}>results per page</Typography>
    </Box>
  );
};

interface GoToPageProps {
  onPageChange: OnPageChange;
  pageInput: string;
  onPageInputChange: OnPageInputChange;
}

const GoToPage = ({ onPageChange, pageInput, onPageInputChange }: GoToPageProps): JSX.Element => {
  const apiRef = useGridApiContext();
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);
  const [pageInputInternal, setPageInput] = useState(pageInput);
  const { i18n } = useLocales();

  const onPageChangeDebounced = useMemo(
    () =>
      debounce((newPage: number, input: string) => {
        if (newPage > pageCount || newPage < 1) return;
        onPageChange(newPage);
        onPageInputChange(input);
      }, 600),
    [onPageChange, onPageInputChange, pageCount]
  );

  const isError =
    !isNaN(parseInt(pageInputInternal)) &&
    (Number(pageInputInternal) > pageCount || Number(pageInputInternal) < 1) &&
    pageCount !== 0;

  return (
    <Box display={'flex'} flexDirection={'row'} alignItems={'center'}>
      <Typography sx={{ typography: 'body2' }}>Go to Page</Typography>
      <Tooltip title={isError ? i18n('pagination.outOfRange') : ''}>
        <span>
          <TextField
            className="paginationInput"
            value={pageInputInternal}
            autoComplete="off"
            error={isError}
            type="number"
            InputProps={{
              sx: {
                width: '30px',
                height: '30px',
                typography: 'body2',
                fontWeight: 'bold',
                padding: '0 !important',
                '& input': {
                  padding: '2px 1px !important',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  width: '100%',
                },
              },
            }}
            inputProps={{
              style: { 
                textAlign: 'center',
                padding: '2px 1px',
              },
            }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const input = event.target.value;
              setPageInput(input);
              const newPage = Number(event.target.value);
              onPageChangeDebounced(newPage, input);
            }}
            sx={{
              marginLeft: 1,
              minWidth: '30px',
              width: '30px',
            }}
          />
        </span>
      </Tooltip>
      <Typography sx={{ marginLeft: 1, typography: 'body2' }}>
        of <span style={{ fontWeight: 'bold' }}>{pageCount}</span>{' '}
      </Typography>
    </Box>
  );
};

export default CustomDataGridPagination;
